import { HUBSPOT_DOWNLOAD_FORM_ID } from "../api/hubspot";
import AiLpFormPage from "./AiLpFormPage";

// 実務資料のダウンロードページ。
//
// 以前は3点セット（規程ひな形・チェックリスト・A4ガイドライン）を配っていた。
// 取り下げた理由は2つ。規程ひな形とA4ガイドラインはJDLA等の無料ひな形と
// 役割が重複していて、より権威のある無料版と比較される土俵に自分から乗っていた。
// チェックリストは45問すべてに合格ラインが無く、設問を配っているだけで、
// 受け取った人の宿題が45個増えるだけの資料だった。
//
// 差し替えたのは、同じ問いに実際の答えを入れた比較表。
// 主要4サービスの公式ドキュメントを一次で当たって埋めてある。
// 設問は自分で作れるが、答えは実際に確認しないと分からない。そこを配る。
//
// /document（サービス資料）とは中身が別物なので分けている。
// あちらは「サービス概要・導入事例・料金感」、こちらは社内で使う実務資料。
// メディア記事（/media/）のCTAはこのページに着地する。
//
// 配布は完了画面で直接渡す。
//
// 当初はHubSpotのフォローアップメールで送る想定だったが、
// 自動化用メールの作成・ワークフローの公開・配信登録の条件と、
// 動かすまでの手数が多い。メールを待たせるぶん離脱もする。
// リードはフォーム送信の時点でHubSpotに記録されるので、
// 配布そのものはこちらで完結させる。
//
// 送信先は資料DL専用フォーム（HUBSPOT_DOWNLOAD_FORM_ID）。
// 共通フォームは4ページで使い回しているため、あとから
// 資料請求者だけを抽出できるよう分けてある。

const PRIMARY = "#2B7FFF";
const INK = "#1B2330";
const round = "'M PLUS Rounded 1c', sans-serif";

// スタックで見せる紙面。読ませるためではなく分量を伝えるためのもの。
//
// scripts/render-doc-preview.py で、配布しているMarkdownそのものから
// 生成している。docx側と同じ変換関数を通しているので、
// 見せている紙面と配っている中身がずれない。資料を更新したら撮り直すこと。
//
// ページ数は書かないこと。プレビュー用CSSとdocx用CSSでフォントメトリクスと
// 余白が違うため、同じ内容でもページ数が一致しない（現状 4 と 8）。
// 旧版は「全13条・3ページ」と書いて実際は5ページだった。
// レンダラに依存しない、数えて検証できる値（表の数・出典の数）だけを出す。
const SHEETS = [
  "sheet-table-1.png",
  "sheet-table-2.png",
  "sheet-table-3.png",
  "sheet-table-4.png",
];

// 完了画面で直接渡すファイル。public/downloads/ に置いてある。
// 相対パスにしてSTGと本番の両方で通るようにする。
const CONTENTS = [
  {
    title: "主要4サービスのAI契約条件（2026年9月版）",
    meta: "比較表6点 ／ 出典14件 ／ Word・Excel",
    files: [
      { label: "Word", href: "/downloads/leaps-ai-terms-table-2026-09.docx" },
      { label: "Excel", href: "/downloads/leaps-ai-terms-table-2026-09.xlsx" },
    ],
    desc: "ChatGPT Business / Enterprise、Microsoft Copilot、Gemini for Workspace、Claude for Work の4サービスについて、学習利用・評価ボタンの扱い・保存期間・保存先と処理先・人によるレビュー・拡張機能の別条件を、公式ドキュメントから埋めた表です。Excel版はサービスで絞り込めます。",
  },
];

// 資料の要点。中身が読まれる前に、何が書いてあるかを示す。
// 抽象的な効能ではなく、表から取った事実をそのまま出す。
const FINDINGS = [
  "「評価ボタンを押すと学習に使われる」のは4社中1社だけ",
  "データを日本国内に閉じることは、どのサービスでもできない",
  "Claude for Work の Team プランは、会話が既定で無期限保存",
  "ChatGPT は Business と Enterprise で、会話を見うる人が変わる",
];

// 扇状に重ねる。中身を読ませないよう小さく置く。
function sheetStyle(i, total) {
  const mid = (total - 1) / 2;
  const offset = i - mid;
  return {
    position: "absolute",
    top: 0,
    left: "50%",
    width: 116,
    marginLeft: -58,
    transform: `translateX(${offset * 52}px) rotate(${offset * 3.4}deg) scale(${
      1 - Math.abs(offset) * 0.035
    })`,
    transformOrigin: "50% 100%",
    background: "#fff",
    border: "1px solid #D5DDE9",
    borderRadius: 4,
    boxShadow: "0 8px 24px rgba(27,35,48,0.18)",
    zIndex: 10 - Math.abs(Math.round(offset)),
  };
}

function DocStack() {
  return (
    <div
      style={{
        position: "relative",
        height: 176,
        marginBottom: 28,
      }}
      aria-hidden="true"
    >
      {SHEETS.map((f, i) => (
        <img
          key={f}
          src={`/images/downloads/${f}`}
          alt=""
          loading="lazy"
          width={635}
          height={898}
          style={{ ...sheetStyle(i, SHEETS.length), height: "auto" }}
        />
      ))}
      {/* 下端をぼかして「まだ続く」ことを示す */}
      <div
        style={{
          position: "absolute",
          inset: "auto 0 0 0",
          height: 64,
          background: "linear-gradient(transparent, #fff 78%)",
          zIndex: 20,
        }}
      />
      <span
        style={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 21,
          background: INK,
          color: "#fff",
          fontFamily: round,
          fontSize: 12,
          fontWeight: 700,
          padding: "5px 14px",
          borderRadius: 999,
          whiteSpace: "nowrap",
        }}
      >
        比較表6点・Word / Excel
      </span>
    </div>
  );
}

function Intro() {
  return (
    <div style={{ marginBottom: 36 }}>
      <DocStack />

      <div
        style={{
          background: "#F8FAFD",
          border: "1px solid #EDF1F7",
          borderRadius: 16,
          padding: "18px 20px",
          marginBottom: 14,
        }}
      >
        <p
          style={{
            fontFamily: round,
            fontSize: 13,
            fontWeight: 800,
            color: INK,
            margin: "0 0 10px",
          }}
        >
          たとえば、こういうことが書いてあります
        </p>
        <ul
          style={{
            margin: 0,
            paddingLeft: "1.15em",
            display: "flex",
            flexDirection: "column",
            gap: 7,
          }}
        >
          {FINDINGS.map((f) => (
            <li
              key={f}
              style={{ fontSize: 13, color: "#5B6472", lineHeight: 1.75 }}
            >
              {f}
            </li>
          ))}
        </ul>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {CONTENTS.map((c) => (
          <div
            key={c.title}
            style={{
              background: "#fff",
              border: "1px solid #F0F3F8",
              borderRadius: 16,
              padding: "18px 20px",
              boxShadow: "0 6px 20px rgba(27,35,48,0.05)",
            }}
          >
            <h2
              style={{
                fontFamily: round,
                fontSize: 16,
                fontWeight: 800,
                margin: 0,
                color: INK,
                lineHeight: 1.5,
              }}
            >
              {c.title}
            </h2>
            <p
              style={{
                fontFamily: round,
                fontSize: 12,
                fontWeight: 700,
                color: PRIMARY,
                margin: "8px 0 0",
              }}
            >
              {c.meta}
            </p>
            <p
              style={{
                fontSize: 13.5,
                color: "#5B6472",
                margin: "8px 0 0",
                lineHeight: 1.8,
              }}
            >
              {c.desc}
            </p>
          </div>
        ))}
      </div>

      <p
        style={{
          fontSize: 12.5,
          color: "#8A94A6",
          margin: "18px 0 0",
          textAlign: "center",
          lineHeight: 1.8,
        }}
      >
        編集して自由にご利用いただけます。
        <br />
        営業のご連絡が不要な場合は、備考欄にその旨をご記入ください。
      </p>
    </div>
  );
}

function DoneExtra() {
  return (
    <div style={{ marginTop: 26, textAlign: "left" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CONTENTS.map((c) => (
          <div
            key={c.title}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 14,
              flexWrap: "wrap",
              background: "#F8FAFD",
              border: "1px solid #EDF1F7",
              borderRadius: 14,
              padding: "14px 16px",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <p
                style={{
                  fontFamily: round,
                  fontSize: 14.5,
                  fontWeight: 800,
                  color: INK,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {c.title}
              </p>
              <p
                style={{
                  fontSize: 11.5,
                  color: "#8A94A6",
                  margin: "3px 0 0",
                }}
              >
                {c.meta}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flex: "none" }}>
              {c.files.map((f) => (
                <a
                  key={f.href}
                  href={f.href}
                  download
                  style={{
                    fontFamily: round,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    background: PRIMARY,
                    padding: "8px 16px",
                    borderRadius: 10,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.label}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p
        style={{
          fontSize: 12.5,
          color: "#8A94A6",
          margin: "18px 0 0",
          lineHeight: 1.9,
          textAlign: "center",
        }}
      >
        このページを閉じたあとで必要になった場合は、
        <br />
        <a href="mailto:info@leaps-inc.com" style={{ color: PRIMARY }}>
          info@leaps-inc.com
        </a>{" "}
        までご連絡ください。あらためてお送りします。
      </p>
    </div>
  );
}

function AiAdoptionDownload() {
  return (
    <AiLpFormPage
      formId={HUBSPOT_DOWNLOAD_FORM_ID || undefined}
      leadKind="download"
      eyebrow="無料ダウンロード"
      title="AIサービスの契約条件を、確認して埋めた表"
      lead="学習利用・保存期間・保存先・人によるレビュー。主要4サービスの公式ドキュメントを一次で当たって、実際の答えを入れています。フォーム送信後、すぐにダウンロードいただけます。"
      intro={<Intro />}
      fields={[
        { name: "company", label: "会社名", type: "text", required: true },
        { name: "name", label: "お名前", type: "text", required: true },
        {
          name: "email",
          label: "メールアドレス",
          type: "email",
          required: true,
        },
        { name: "tel", label: "電話番号", type: "tel", required: false },
        { name: "message", label: "備考（任意）", type: "textarea" },
      ]}
      footnote="ご入力の情報は資料のご案内以外には使用しません"
      submitLabel="ダウンロードする"
      doneTitle="ダウンロードいただけます"
      doneMsg="ありがとうございます。下記からご利用ください。"
      doneExtra={<DoneExtra />}
    />
  );
}

export default AiAdoptionDownload;
